import { ManagementCompanyUsageType } from "@/modules/common/types/ManagementCompanies.enum";
import z from "zod";

export const digitsOnly = (value: string) => value.replace(/\D/g, "");

const optionalEmailSchema = z
  .string()
  .trim()
  .refine((value) => value === "" || z.email().safeParse(value).success, "O e-mail deve ser válido")
  .optional();

// Mirrors the backend rule (CreateManagementCompanyOwnerDto): min 8 chars, upper+lower case, number and special char.
const OWNER_PASSWORD_RULE = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;

const managementCompanySchema = z.object({
  name: z.string().trim().min(1, "É necessário informar o nome fantasia!"),
  corporateName: z.string().trim().optional(),
  // A demonstration gestora has no legal entity behind it, so the requirement is enforced per usage type below.
  cnpj: z
    .string()
    .trim()
    .refine((value) => value === "" || digitsOnly(value).length === 14, "O CNPJ deve ser válido")
    .optional(),
  email: optionalEmailSchema,
  managementModel: z.string().min(1, "É necessário selecionar o modelo de gestão!"),
  status: z.string().optional(),
});

const isDemonstration = (usageType: unknown) => Number(usageType) === ManagementCompanyUsageType.DEMONSTRATION;

const requireCnpjOutsideDemonstration = (values: { cnpj?: string; usageType?: unknown }, ctx: z.RefinementCtx) => {
  if (isDemonstration(values.usageType) || values.cnpj?.trim()) return;

  ctx.addIssue({ code: "custom", path: ["cnpj"], message: "É necessário informar o CNPJ!" });
};

export const createManagementCompanySchema = managementCompanySchema
  .extend({
    usageType: z.enum(ManagementCompanyUsageType).optional(),
    demoConfiguration: z
      .object({
        modules: z.array(z.string()),
      })
      .optional(),
    ownerName: z.string().trim().min(1, "É necessário informar o nome do representante!"),
    ownerEmail: z
      .string()
      .trim()
      .min(1, "É necessário informar o e-mail do representante!")
      .refine((value) => z.email().safeParse(value).success, "O e-mail deve ser válido"),
    ownerPhoneNumber: z.string().trim().optional(),
    // Only demonstration gestoras define it — production ones keep receiving a first access code by e-mail.
    ownerPassword: z.string().optional(),
    ownerPasswordConfirmation: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    requireCnpjOutsideDemonstration(values, ctx);

    if (!isDemonstration(values.usageType)) return;

    if (!values.ownerPassword) {
      ctx.addIssue({ code: "custom", path: ["ownerPassword"], message: "É necessário informar a senha do representante!" });
    } else if (!OWNER_PASSWORD_RULE.test(values.ownerPassword)) {
      ctx.addIssue({
        code: "custom",
        path: ["ownerPassword"],
        message: "A senha deve ter no mínimo 8 caracteres, com letra maiúscula, minúscula, número e caractere especial",
      });
    }

    if (!values.ownerPasswordConfirmation) {
      ctx.addIssue({ code: "custom", path: ["ownerPasswordConfirmation"], message: "É necessário confirmar a senha do representante!" });
      return;
    }

    if (values.ownerPassword !== values.ownerPasswordConfirmation) {
      ctx.addIssue({ code: "custom", path: ["ownerPasswordConfirmation"], message: "As senhas não coincidem" });
    }
  });

export const updateManagementCompanySchema = managementCompanySchema
  .extend({
    status: z.string().min(1, "É necessário selecionar o status!"),
    // Not editable here, it only tells whether the CNPJ is required for this gestora.
    usageType: z.enum(ManagementCompanyUsageType).optional(),
    ownerId: z.string().min(1, "É necessário selecionar o representante principal!"),
  })
  .superRefine(requireCnpjOutsideDemonstration);

export type ManagementCompanyBaseFormData = z.infer<typeof managementCompanySchema>;
export type CreateManagementCompanyFormData = z.infer<typeof createManagementCompanySchema>;
export type UpdateManagementCompanyFormData = z.infer<typeof updateManagementCompanySchema>;
